import { Component, HostListener } from "@angular/core";
import {
  ImageFlipperService,
  ImageFlipperServiceType,
} from "../image-flipper.service";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { NzNotificationService } from "ng-zorro-antd/notification";

@Component({
    selector: "app-copilot-image-flipper",
    templateUrl: "./copilot-image-flipper.component.html",
    styleUrls: ["./copilot-image-flipper.component.less"],
    standalone: false
})
export class CopilotImageFlipperComponent {
  private destroyed$ = new Subject<void>();
  drawer_visible: boolean = false;
  drawer_width: string = "fit-content";
  images: ImageFlipperServiceType[] = [];

  current_index = 0;
  // when true the stage image stretches to fill the stage (object-fit: fill)
  // instead of preserving its aspect ratio (object-fit: contain)
  fill_image = false;
  is_playing = false;
  flip_interval_ms = 1000;
  speed_options = [
    { label: "Slow (2s)", value: 2000 },
    { label: "Normal (1s)", value: 1000 },
    { label: "Fast (0.5s)", value: 500 },
    { label: "Very fast (0.25s)", value: 250 },
  ];
  private play_timer: any = null;

  constructor(
    private imageFlipper: ImageFlipperService,
    private notificationService: NzNotificationService
  ) {}

  ngOnInit() {
    this.imageFlipper.images$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (images) => {
        this.images = images;
        // keep the current frame in range when images are removed
        this.current_index = Math.min(
          this.current_index,
          Math.max(0, this.images.length - 1)
        );
        if (this.images.length == 0) {
          this.drawer_width = "fit-content";
          this.stopPlayback();
        } else {
          this.drawer_width = "80%";
        }
      },
      error: (err) => {
        console.log(err);
        this.notificationService.error(
          "Error",
          err.error?.error ||
            "There was an error when getting all projects. Please try later"
        );
      },
    });
  }

  ngOnDestroy() {
    this.stopPlayback();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get currentImage(): ImageFlipperServiceType | null {
    return this.images[this.current_index] || null;
  }

  go_to_image(index: number) {
    this.current_index = index;
  }

  next() {
    if (this.images.length === 0) {
      return;
    }
    this.current_index = (this.current_index + 1) % this.images.length;
  }

  prev() {
    if (this.images.length === 0) {
      return;
    }
    this.current_index =
      (this.current_index - 1 + this.images.length) % this.images.length;
  }

  togglePlayback() {
    if (this.is_playing) {
      this.stopPlayback();
    } else {
      this.startPlayback();
    }
  }

  private startPlayback() {
    if (this.images.length < 2) {
      return;
    }
    this.is_playing = true;
    this.play_timer = setInterval(() => this.next(), this.flip_interval_ms);
  }

  private stopPlayback() {
    this.is_playing = false;
    if (this.play_timer) {
      clearInterval(this.play_timer);
      this.play_timer = null;
    }
  }

  onSpeedChange() {
    // restart the timer with the new interval
    if (this.is_playing) {
      this.stopPlayback();
      this.startPlayback();
    }
  }

  remove_image(index: number) {
    this.imageFlipper.remove_image_at_index(index);
  }

  clear_all() {
    this.imageFlipper.clear_images();
  }

  @HostListener("document:keydown", ["$event"])
  handleKeydown(event: KeyboardEvent) {
    if (!this.drawer_visible || this.images.length === 0) {
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.next();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.prev();
    }
  }
}
